import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountService, ResetPasswordDto } from '@abp/ng.account.core/proxy';
import { finalize } from 'rxjs/operators';
import { LocalizationService } from '@abp/ng.core';
import { ActivatedRoute, Router } from '@angular/router';
import { MetroToastService } from '../shared/services/metro-toast.service';

@Component({
  selector: 'app-reset-password',
  standalone: false,
  templateUrl: './reset-password.component.html'
})
export class ResetPasswordCustomComponent implements OnInit {
  resetPasswordForm: FormGroup;
  inProgress = false;

  private l = inject(LocalizationService);

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
    private toasterService: MetroToastService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.buildForm();
  }

  protected buildForm() {
    this.resetPasswordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(128)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(128)]],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  private passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  onSubmit() {
    if (this.inProgress || this.resetPasswordForm.invalid) return;

    this.inProgress = true;
    const { password } = this.resetPasswordForm.value;

    // Try different parameter names that ABP might use
    const token = 
      this.route.snapshot.queryParams['Token'] || 
      this.route.snapshot.queryParams['t'] ||
      this.route.snapshot.queryParams['resetToken'];
    const userId = 
      this.route.snapshot.queryParams['UserId'] || 
      this.route.snapshot.queryParams['userId'];

    // In some ABP configurations, the reset token might contain the email instead
    const email = 
      this.route.snapshot.queryParams['Email'] || 
      this.route.snapshot.queryParams['email'];

    if (!token) {
      this.toasterService.error(this.l.instant('::MissingResetPasswordParameters'));
      this.inProgress = false;
      return;
    }

    // Prepare the reset password request
    const request: ResetPasswordDto = {
      resetToken: token,
      password: password
    };

    // Add userId if available
    if (userId) {
      request.userId = userId;
    }

    this.accountService
      .resetPassword(request)
      .pipe(finalize(() => (this.inProgress = false)))
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.l.instant('::YourPasswordHasChangedSuccessfully')
          );
          this.router.navigate(['/account/login']);
        },
        error: (error) => {
          const message =
            error?.error?.error?.message ||
            error?.error?.error_description ||
            this.l.instant('::PasswordResetError');
          this.toasterService.error(message);
        },
      });
  }
}